import React from 'react';
import type { Game } from '@sulis-liiga/common';
import { GameStatus } from '@sulis-liiga/common';
import { teamLabel, teamLabelsOf, gamesWon, scoreFor } from '../../utils/gameTeams';

interface GameInfoProps {
  game: Game;
}

const GameInfo: React.FC<GameInfoProps> = ({ game }) => {
  const formattedDate = game.scheduledDate
    ? new Date(game.scheduledDate).toLocaleDateString()
    : 'Not scheduled yet';

  const [teamALabel, teamBLabel] = teamLabelsOf(game);
  const result = game.result;
  const sets = result?.sets ?? [];
  const winnerLabel = result ? teamLabel(game, result.winner) : null;
  const retiredLabel = result?.retiredTeamId ? teamLabel(game, result.retiredTeamId) : null;
  // Always render three game columns so the grid matches the entry/approve pages,
  // padding with blanks when fewer sets were played.
  const setColumns = Math.max(sets.length, 3);

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex justify-between items-start mb-6">
        <h2 className="text-xl font-bold text-gray-900">Game Details</h2>
        <span
          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            game.status === GameStatus.SCHEDULED
              ? 'bg-blue-100 text-blue-800'
              : game.status === GameStatus.COMPLETED
              ? 'bg-green-100 text-green-800'
              : game.status === GameStatus.DISPUTED
              ? 'bg-yellow-100 text-yellow-800'
              : game.status === GameStatus.CANCELLED && game.cancellationReason === 'abandoned'
              ? 'bg-amber-100 text-amber-800'
              : 'bg-gray-100 text-gray-800'
          }`}
        >
          {game.status === GameStatus.CANCELLED && game.cancellationReason === 'abandoned'
            ? 'Abandoned'
            : game.status}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h3 className="text-sm font-medium text-gray-500">Teams</h3>
          <p className="mt-1 text-sm text-gray-900">
            {teamALabel} <span className="text-gray-400">vs</span> {teamBLabel}
          </p>
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-500">Date &amp; Time</h3>
          <p className="mt-1 text-sm text-gray-900">
            {formattedDate} at {game.scheduledTime}
          </p>
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-500">Venue</h3>
          <p className="mt-1 text-sm text-gray-900">{game.venue?.name ?? '—'}</p>
          <p className="text-sm text-gray-500">{game.venue?.address ?? ''}</p>
        </div>
      </div>

      {result && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Result</h3>

          {retiredLabel && (
            <div className="mb-3 rounded-md bg-amber-50 text-amber-800 text-sm px-3 py-2">
              <span className="font-semibold">Retirement</span> — {retiredLabel} retired; the score
              shows what was played up to that point.
            </div>
          )}

          {/* Score grid — teams as rows, games as columns (matches the entry/approve pages) */}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-gray-500">
                <tr>
                  <th className="text-left px-3 py-1 font-medium" />
                  {Array.from({ length: setColumns }, (_, i) => (
                    <th key={i} className="px-3 py-1 font-medium text-center">
                      G{i + 1}
                    </th>
                  ))}
                  <th className="px-3 py-1 font-medium text-center">Games</th>
                </tr>
              </thead>
              <tbody>
                {game.teamIds.map(teamId => {
                  const isWinner = result.winner === teamId;
                  return (
                    <tr
                      key={teamId}
                      className={`border-t border-gray-100 ${isWinner ? 'bg-success-50' : ''}`}
                    >
                      <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">
                        {teamLabel(game, teamId)}
                      </td>
                      {Array.from({ length: setColumns }, (_, i) => (
                        <td key={i} className="px-3 py-2 text-center">
                          {sets[i] ? scoreFor(sets[i], teamId) : '—'}
                        </td>
                      ))}
                      <td
                        className={`px-3 py-2 text-center font-bold ${
                          isWinner ? 'text-success-800' : 'text-gray-500'
                        }`}
                      >
                        {gamesWon(result, teamId)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="mt-3 text-sm text-gray-700">
            Winner: <span className="font-semibold text-success-800">{winnerLabel}</span>
            {game.pointsAwarded && (
              <>
                {' · '}points: {teamALabel} +{scoreFor(game.pointsAwarded, game.teamIds[0])},{' '}
                {teamBLabel} +{scoreFor(game.pointsAwarded, game.teamIds[1])}
              </>
            )}
          </p>
        </div>
      )}

      {game.notes && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-sm font-medium text-gray-500">Notes</h3>
          {/* An append-only history (free-text notes + dispute/resolution/reschedule
              events), one entry per line — render the line breaks. */}
          <p className="mt-1 text-sm text-gray-900 whitespace-pre-line">{game.notes}</p>
        </div>
      )}
    </div>
  );
};

export default GameInfo;
